import * as React from 'react';
import { useSelector } from 'react-redux';

import { useBrokerAnalyticsUser, useBrokerConfig, useBrokerWidgetAPI } from './hooks';
import { trackSellApartmentClick, trackFallbackModalOpen, trackFallbackModalRetryClick } from './tracking';
import { BrokerFallbackModal } from '../../components/BrokerFallbackModal';
import { NewbuildingBrokerButton } from '../../components/NewbuildingBrokerButton';
import {
  getNewbuildingId,
  getRegionId,
  getBrokerFlow,
  getBrokerPartnerRewardsLanding,
} from '../../selectors/newbuilding';
import { getIsSelfEmployed } from '../../selectors/user';
import { TBrokerFlow, TOpenWidgetResultHandler } from '../../types/broker';
import { NewbuildingBrokerWidgetContainer } from '../NewbuildingBrokerWidget';

export function BrokerButton() {
  const flowType = useSelector(getBrokerFlow);
  const newbuildingId = useSelector(getNewbuildingId);
  const newbuildingRegionId = useSelector(getRegionId);
  const isSelfEmployed = useSelector(getIsSelfEmployed);
  const partnerRewardsLanding = useSelector(getBrokerPartnerRewardsLanding);

  const { enabled } = useBrokerConfig();
  const user = useBrokerAnalyticsUser(flowType);

  const [loading, setLoading] = React.useState(false);
  const [isFallbackModalVisible, setFallbackModalVisibility] = React.useState(false);
  const closeFallbackModal = React.useCallback(() => setFallbackModalVisibility(false), []);
  const showFallbackModal = React.useCallback(() => setFallbackModalVisibility(true), []);

  const handleWidgetOpeningFinish = React.useCallback<TOpenWidgetResultHandler>(
    ({ isError }) => {
      if (isError) {
        trackFallbackModalOpen(user);
        showFallbackModal();
      }
    },
    [showFallbackModal, user],
  );

  const { openWidget, ...widgetParams } = useBrokerWidgetAPI(handleWidgetOpeningFinish);

  const openLandingOrWidget = React.useCallback(async () => {
    if (partnerRewardsLanding) {
      window.open(partnerRewardsLanding, '_blank', 'noopener');
    } else {
      setLoading(true);

      await openWidget({
        flowType: flowType as TBrokerFlow,
        newbuildingId,
        newbuildingRegionId,
        houseId: null,
        isSelfEmployed,
        trackingParams: {
          pageType: 'CardJK',
          siteType: 'desktop',
          user,
        },
      });

      setLoading(false);
    }
  }, [flowType, isSelfEmployed, newbuildingId, newbuildingRegionId, openWidget, partnerRewardsLanding, user]);

  const handleButtonClick = React.useCallback(async () => {
    trackSellApartmentClick(user);
    await openLandingOrWidget();
  }, [openLandingOrWidget, user]);

  const handleRetryClick = React.useCallback(() => {
    trackFallbackModalRetryClick(user);
    closeFallbackModal();
    openLandingOrWidget();
  }, [closeFallbackModal, openLandingOrWidget, user]);

  if (!enabled || !flowType) {
    return null;
  }

  return (
    <>
      <NewbuildingBrokerButton loading={loading} onClick={handleButtonClick} />
      <BrokerFallbackModal isOpen={isFallbackModalVisible} onClose={closeFallbackModal} onRetry={handleRetryClick} />
      <NewbuildingBrokerWidgetContainer {...widgetParams} />
    </>
  );
}
